// Stillhouse view — Access & identity. The "Identity providers" tab is LIVE: it
// manages the Dex broker's upstream connectors (Google / Microsoft 365 / generic
// OIDC) through /settings/idp — provider CRUD, an apply pipeline with a live sync
// pill, and the "use this broker for Stillhouse sign-in" switch. SCIM / 2FA / IP
// tabs remain static design mocks.
import React, { useCallback, useEffect, useState } from "react";
import { C, F, Pill, Btn } from "../brand/apo";
import { Card, StatusDot, Chip, Toggle, Tabs, PageHeader, Stat, EmptyState, COPPER } from "../brand/sh";
import { Modal } from "../components/Modal";
import { IdpProviderForm } from "../components/IdpProviderForm";
import { api, type IdpConfig, type IdpProvider, type IdpSyncStatus, type IdpType } from "../api";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);

function Row({ title, sub, children, last }: { title: React.ReactNode; sub?: React.ReactNode; children?: React.ReactNode; last?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "15px 18px", borderBottom: last ? "none" : `1px solid ${C.line}` }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, color: C.fgDark, fontWeight: 500 }}>{title}</div>
        {sub && <div style={{ fontSize: 12.5, color: C.fgMute, marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

// Backend serialises the enum; normalise case so the pill is robust either way.
function normSync(s: string | undefined | null): IdpSyncStatus {
  const u = (s ?? "").toUpperCase();
  return u === "APPLIED" || u === "PENDING" || u === "ERROR" ? u : "PENDING";
}
const SYNC_COLOR: Record<IdpSyncStatus, string> = { APPLIED: C.glow, PENDING: C.amber, ERROR: C.rose };
const TYPE_COLOR: Record<IdpType, string> = { GOOGLE: C.glow, MICROSOFT: C.lilac, OIDC: COPPER };
const TYPE_LABEL: Record<IdpType, string> = { GOOGLE: "Google", MICROSOFT: "M365", OIDC: "OIDC" };

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

function Copyable({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div style={{ position: "relative", background: C.ink, border: `1px solid ${C.line}`, padding: "11px 12px" }}>
      <code style={{ fontFamily: F.mono, fontSize: 12, color: C.glow, wordBreak: "break-all", display: "block", paddingRight: 56 }}>{text}</code>
      <button
        onClick={copy}
        style={{ position: "absolute", top: 8, right: 8, fontFamily: F.mono, fontSize: 10, letterSpacing: "0.1em", color: copied ? C.ink : C.fgDark, background: copied ? C.glow : C.surfaceHi, border: `1px solid ${copied ? C.glow : C.line}`, padding: "4px 9px", cursor: "pointer", textTransform: "uppercase" }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export function AccessIdentity() {
  const [tab, setTab] = useState("idp");
  const [config, setConfig] = useState<IdpConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<IdpProvider | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IdpProvider | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [loginSaving, setLoginSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setConfig(await api.getIdp());
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const broker = config?.broker ?? null;
  const providers = config?.providers ?? [];
  const sync = broker ? normSync(broker.syncStatus) : null;
  const redirectUri = broker?.issuerUrl ? `${broker.issuerUrl.replace(/\/$/, "")}/callback` : null;

  // Reflect the latest sync status without a full reload — cheap poll after apply.
  const patchSync = (status: IdpSyncStatus, lastError: string | null) =>
    setConfig((prev) => (prev ? { ...prev, broker: { ...prev.broker, syncStatus: status, lastError } } : prev));

  const apply = async () => {
    if (applying) return;
    setApplying(true);
    setError(null);
    try {
      const r = await api.applyIdp();
      patchSync(normSync(r.syncStatus), r.error ?? null);
      // Apply may settle asynchronously (Dex restart) — poll status until it
      // leaves PENDING or the bounded window elapses.
      for (let i = 0; i < 8 && normSync(r.syncStatus) === "PENDING"; i++) {
        await delay(1500);
        try {
          const st = await api.idpStatus();
          patchSync(normSync(st.syncStatus), st.lastError ?? null);
          if (normSync(st.syncStatus) !== "PENDING") break;
        } catch {
          /* transient — keep polling */
        }
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setApplying(false);
    }
  };

  const toggleEnabled = async (p: IdpProvider) => {
    setSavingId(p.id);
    setError(null);
    try {
      await api.updateIdpProvider(p.id, { enabled: !p.enabled });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (p: IdpProvider) => {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteIdpProvider(p.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteTarget(null);
      await load();
      setError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const setStillhouseLogin = async (enabled: boolean) => {
    setLoginSaving(true);
    setError(null);
    try {
      await api.setStillhouseLogin(enabled);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoginSaving(false);
    }
  };

  const cols = "0.7fr 1.3fr 1.7fr 0.7fr 1fr";

  return (
    <React.Fragment>
      <PageHeader
        crumb="GOVERNANCE › ACCESS"
        title="Access & identity"
        sub="Single sign-on, automated provisioning and the second factor — the front door to every MoonShine machine."
      >
        {sync && (
          <Pill color={SYNC_COLOR[sync]} size="sm" glow={sync === "APPLIED"}>
            IDP · {sync}
          </Pill>
        )}
      </PageHeader>
      <div style={{ padding: "0 32px", borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: "idp", label: "Identity providers" },
            { id: "scim", label: "SCIM provisioning" },
            { id: "twofa", label: "Two-factor" },
            { id: "ip", label: "IP allowlist" },
          ]}
        />
      </div>
      <Scroll>
        {tab === "idp" && (
          <React.Fragment>
            {error && (
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.rose, letterSpacing: "0.04em" }}>{error}</div>
            )}
            {sync === "PENDING" && (
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.amber, letterSpacing: "0.04em" }}>
                Broker config not yet applied — click Apply configuration to render it into Dex.
              </div>
            )}
            {sync === "ERROR" && broker?.lastError && (
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.rose, letterSpacing: "0.04em" }}>
                Last apply failed: {broker.lastError}
              </div>
            )}

            <Card
              title="Identity providers"
              tag={sync ? <Chip color={SYNC_COLOR[sync]}>{sync}</Chip> : undefined}
              pad={false}
              action={
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn ghost size="md" style={{ padding: "5px 14px", fontSize: 12 }} onClick={() => setAddOpen(true)}>
                    Add provider
                  </Btn>
                  <Btn primary size="md" style={{ padding: "5px 14px", fontSize: 12, opacity: applying ? 0.7 : 1 }} onClick={() => void apply()}>
                    {applying ? "Applying…" : "Apply configuration"}
                  </Btn>
                </div>
              }
            >
              {providers.length === 0 ? (
                <div style={{ padding: "10px 0" }}>
                  <EmptyState
                    title="No identity providers yet"
                    sub="ADD GOOGLE, MICROSOFT 365, OR A GENERIC OIDC PROVIDER TO GET STARTED"
                  />
                </div>
              ) : (
                <div style={{ margin: "-16px -18px 0" }}>
                  <div style={{ display: "grid", gridTemplateColumns: cols, gap: 14, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
                    <span>TYPE</span>
                    <span>DISPLAY NAME</span>
                    <span>CLIENT ID</span>
                    <span>ENABLED</span>
                    <span style={{ textAlign: "right" }}>ACTIONS</span>
                  </div>
                  {providers.map((p, i) => (
                    <div key={p.id} style={{ display: "grid", gridTemplateColumns: cols, gap: 14, padding: "12px 18px", borderBottom: i < providers.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "center", fontFamily: F.ui, fontSize: 13 }}>
                      <span>
                        <Chip color={TYPE_COLOR[p.type]}>{TYPE_LABEL[p.type]}</Chip>
                      </span>
                      <span style={{ color: C.fgDark, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.displayName}</span>
                      <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgMute, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.clientId}</span>
                      <span>
                        <Toggle on={p.enabled} disabled={savingId === p.id} onChange={() => void toggleEnabled(p)} />
                      </span>
                      <span style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <Btn ghost size="md" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => setEditing(p)}>
                          Edit
                        </Btn>
                        <Btn ghost size="md" style={{ padding: "5px 12px", fontSize: 12, color: C.rose, borderColor: `${C.rose}55` }} onClick={() => setDeleteTarget(p)}>
                          Delete
                        </Btn>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {redirectUri && (
              <Card title="Provider setup" tag={<Chip color={C.lilac}>redirect URI</Chip>}>
                <div style={{ fontSize: 13, color: C.fgMute, lineHeight: 1.55, marginBottom: 12 }}>
                  Register this redirect URI in each provider's console (Google Cloud, Azure app registration, or your
                  OIDC client) so the broker can complete the sign-in round-trip.
                </div>
                <Copyable text={redirectUri} />
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, fontFamily: F.mono, fontSize: 11, color: C.fgFaint, letterSpacing: "0.04em" }}>
                  <span style={{ letterSpacing: "0.16em", fontWeight: 600 }}>ISSUER</span>
                  <span style={{ color: C.fgMute, wordBreak: "break-all" }}>{broker?.issuerUrl}</span>
                </div>
              </Card>
            )}

            <Card title="Stillhouse sign-in" pad={false}>
              <Row
                title="Use this broker for Stillhouse sign-in"
                sub="Point the Stillhouse control-plane login at this broker so admins sign in through the same providers."
                last
              >
                <Toggle
                  on={broker?.stillhouseLoginEnabled ?? false}
                  disabled={loginSaving || !broker}
                  onChange={(next) => void setStillhouseLogin(next)}
                />
              </Row>
            </Card>
          </React.Fragment>
        )}
        {tab === "scim" && (
          <React.Fragment>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
              <Stat label="Provisioned" value="—" sub="via SCIM 2.0" accent={C.glow} />
              <Stat label="Pushed groups" value="—" accent={C.lilac} />
              <Stat label="Deprovisioned" value="—" sub="lifetime" accent={C.fgMute} />
              <Stat label="Last sync" value="—" sub="not synced" accent={COPPER} />
            </div>
            <Card title="SCIM 2.0 provisioning" tag={<StatusDot status="online" label="Active" />} pad={false}>
              <Row title="Base URL" sub="Not configured">
                <Btn ghost size="md" style={{ padding: "5px 12px", fontSize: 12 }}>
                  Copy
                </Btn>
              </Row>
              <Row title="Bearer token" sub="Rotated 6 days ago · read/write">
                <Chip color={C.glow}>Active</Chip>
              </Row>
              <Row title="Create users" sub="New IdP assignment → MoonShine account + team">
                <Toggle defaultOn />
              </Row>
              <Row title="Push groups" sub="Mirror IdP groups as Stillhouse groups">
                <Toggle defaultOn />
              </Row>
              <Row title="Deactivate on deprovision" sub="Revoke all sessions + pairings when removed from IdP" last>
                <Toggle defaultOn />
              </Row>
            </Card>
          </React.Fragment>
        )}
        {tab === "twofa" && (
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
            <Card title="Two-factor enforcement" pad={false}>
              <Row title="Require 2FA for all members" sub="Members without a second factor are blocked at next sign-in">
                <Toggle defaultOn />
              </Row>
              <Row title="Require 2FA for admins" sub="Cannot be disabled by policy">
                <Toggle on disabled />
              </Row>
              <Row title="Allow authenticator apps (TOTP)" sub="Google Authenticator, 1Password, Authy">
                <Toggle defaultOn />
              </Row>
              <Row title="Allow hardware security keys" sub="WebAuthn / FIDO2 · YubiKey">
                <Toggle defaultOn />
              </Row>
              <Row title="Allow SMS codes" sub="Discouraged — kept off by default" last>
                <Toggle />
              </Row>
            </Card>
            <Card title="Coverage" pad={false}>
              <EmptyState title="No enrolment data yet" sub="2FA COVERAGE APPEARS ONCE MEMBERS ENROLL" />
            </Card>
          </div>
        )}
        {tab === "ip" && (
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16 }}>
            <Card
              title="Allowed networks"
              tag={<Chip color={C.glow}>Enforced</Chip>}
              pad={false}
              action={
                <Btn primary size="md" style={{ padding: "5px 12px", fontSize: 12 }}>
                  Add range
                </Btn>
              }
            >
              <EmptyState title="No allowed networks yet" sub="ADD A RANGE TO RESTRICT ACCESS BY IP" />
            </Card>
            <Card title="Policy" pad={false}>
              <Row title="Restrict sessions to allowlist" sub="Connections from other IPs are refused">
                <Toggle defaultOn />
              </Row>
              <Row title="Restrict the console too" sub="Stillhouse admin only reachable from allowed ranges">
                <Toggle defaultOn />
              </Row>
              <Row title="Exempt 'offsite' group" sub="Field staff connect via device posture instead">
                <Toggle defaultOn />
              </Row>
              <Row title="Alert on blocked attempt" sub="Notify security-admins in real time" last>
                <Toggle defaultOn />
              </Row>
            </Card>
          </div>
        )}
      </Scroll>

      <Modal
        open={addOpen || editing !== null}
        onClose={() => {
          setAddOpen(false);
          setEditing(null);
        }}
        title={editing ? "Edit provider" : "Add identity provider"}
        sub={editing ? "Update this provider, then apply the configuration to push it to the broker." : "Configure an upstream connector. Apply the configuration afterwards to render it into Dex."}
        width={520}
      >
        <IdpProviderForm
          provider={editing}
          redirectUri={redirectUri}
          onSaved={() => {
            setAddOpen(false);
            setEditing(null);
            void load();
          }}
          onCancel={() => {
            setAddOpen(false);
            setEditing(null);
          }}
        />
      </Modal>

      <Modal
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.displayName ?? "provider"}?`}
        sub="The connector is removed from the broker config. Users who signed in through it can no longer authenticate once you apply the configuration."
        width={480}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn ghost onClick={() => !deleting && setDeleteTarget(null)}>
            Cancel
          </Btn>
          <Btn
            primary
            style={{ background: C.rose, borderColor: C.rose, opacity: deleting ? 0.7 : 1, pointerEvents: deleting ? "none" : "auto" }}
            onClick={() => deleteTarget && void remove(deleteTarget)}
          >
            {deleting ? "Deleting…" : "Delete provider"}
          </Btn>
        </div>
      </Modal>
    </React.Fragment>
  );
}
